import { ApolloClient, ApolloLink, CombinedGraphQLErrors, CombinedProtocolErrors, InMemoryCache } from "@apollo/client";
import "@apollo/client";
import { ErrorLink } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { createClient } from "graphql-ws";
import _ from "lodash";

import { getAuthorizationToken } from "../authorization_token";
import { authLink, GRAPHQL_URI_RS, GRAPHQL_URI_WS_RS, sentryLink } from "../graphqlUtils";
import { setContext } from "../sentry";

declare module "@apollo/client" {
  interface DataMasking {
    mode: "unmask";
  }
}

const httpLinkRs = new UploadHttpLink({ uri: GRAPHQL_URI_RS });
const client = createClient({
  url: GRAPHQL_URI_WS_RS,
  shouldRetry: () => true,
  lazy: true,
  keepAlive: 10_000,
});

const wsLink = new GraphQLWsLink(client);

export function shouldIgnoreGraphQLError(opName: string, message: string) {
  return (
    ["login", "refresh"].includes(opName) ||
    ["forbidden", "unauthorized", "could not find resource"].includes(message.toLowerCase())
  );
}

export const errorLink = new ErrorLink(({ error, operation, result }) => {
  const opName = operation?.operationName || "";
  const opVars = operation?.variables || {};

  if (CombinedGraphQLErrors.is(error)) {
    error.errors
      .filter(({ message }) => message !== "not_found")
      .forEach(({ message, locations, path, extensions }) => {
        if (shouldIgnoreGraphQLError(opName, message)) return;

        console.error(
          `[GraphQL error]: Message: ${message}, Location:`,
          locations,
          "Path:",
          path,
          "extensions",
          extensions,
          "Operation:",
          opName,
          operation,
        );

        if (process.env.NODE_ENV === "production") {
          const variables = _.omit(opVars, ["password", "values.password"]);

          const errorContext = {
            extensions: JSON.stringify(extensions, undefined, 2),
            locations: JSON.stringify(locations, undefined, 2),
            path: JSON.stringify(path, undefined, 2),
            operationName: opName,
            operation: JSON.stringify(result, undefined, 2),
            variables: JSON.stringify(variables, undefined, 2),
          };

          const err = new Error(message);
          err.name = "graphQLError";
          if ("captureStackTrace" in Error) Error.captureStackTrace(err as any);

          Sentry.setTag("type", "graphql");
          Sentry.setContext("graphqlContext", errorContext);
          setContext();
          Sentry.captureException(err);
        }
      });
  } else if (CombinedProtocolErrors.is(error)) {
    error.errors.forEach(({ message, extensions }) => {
      console.error(`[Protocol error]: Message: ${message}, Extensions:`, extensions, "Operation:", opName);
    });

    if (process.env.NODE_ENV === "production") {
      const err = new Error("protocol error");
      err.name = "protocolError";
      if ("captureStackTrace" in Error) Error.captureStackTrace(err as any);

      Sentry.setTag("type", "graphql");
      setContext();
      Sentry.captureException(err);
    }
  } else if (error) {
    console.error("[error]:", error);

    if (process.env.NODE_ENV === "production") {
      const err = error instanceof Error ? error : new Error(String(error));
      err.name ||= "networkError";
      if ("captureStackTrace" in Error && !(err as any).stack) Error.captureStackTrace(err as any);

      Sentry.setTag("type", "network");
      setContext();
      Sentry.captureException(err);
    }
  }
});

const linkRs = ApolloLink.from([
  sentryLink,
  errorLink,
  authLink(() => getAuthorizationToken()).concat(httpLinkRs as any),
]);

const apolloClient = new ApolloClient({
  link: ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === "OperationDefinition" && definition.operation === "subscription";
    },
    wsLink,
    linkRs,
  ),

  cache: new InMemoryCache(),

  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
});

export default apolloClient;
